import { InfoCircleTwoTone } from "@ant-design/icons";
import { Popover } from "antd";

import { Flex } from "@av/components/atoms/layout";
import { EmailCell } from "./EmailCell";
import {
  type AdminSettingsUserBase,
  headerCell,
} from "../../../../../services/admin-settings/type";

import type { ColumnsType } from "antd/lib/table/interface";
import type { TFunction } from "i18next";

type EmailColumnProps<TUser extends AdminSettingsUserBase> = {
  t: TFunction;
  testIdPrefix: string;
  getTestIdValue?: (user: TUser) => string;
  // TODO: src/config/i18n/__tests__/i18n.test.ts only verifies literal t(...) call sites with a
  // quoted key, so it can't see keys passed in through a variable like this -- a typo
  // in either the default below or a caller's override won't be caught by that test.
  // This needs to be addressed in the test.
  tooltipTitleKey?: string;
  tooltipContentKey?: string;
};

export function createEmailColumn<TUser extends AdminSettingsUserBase>({
  t,
  testIdPrefix,
  getTestIdValue,
  tooltipTitleKey = "platform.adminSettings.users_columns_email_tooltip_title",
  tooltipContentKey = "platform.adminSettings.users_columns_email_tooltip_content",
}: EmailColumnProps<TUser>): ColumnsType<TUser>[number] {
  return {
    title: (
      <Flex align="center" gap={4} style={{ paddingInline: 8 }}>
        {t("platform.adminSettings.users_columns_email")}
        <Popover
          title={t(tooltipTitleKey)}
          content={t(tooltipContentKey)}
          trigger={["hover", "focus"]}
        >
          <span
            aria-label={t(tooltipTitleKey)}
            data-testid={`${testIdPrefix}-column-email-info`}
            style={{ display: "inline-flex", lineHeight: 0 }}
            tabIndex={0}
          >
            <InfoCircleTwoTone />
          </span>
        </Popover>
      </Flex>
    ),
    dataIndex: "email",
    key: "email",
    onHeaderCell: headerCell(`${testIdPrefix}-column-email`),
    width: 280,
    render: (email: TUser["email"], user: TUser) => (
      <EmailCell
        email={email}
        getTestIdValue={getTestIdValue}
        testIdPrefix={testIdPrefix}
        t={t}
        user={user}
      />
    ),
  };
}
